'use client';
import * as React from 'react';
import { FloatingPortal } from '../../floating-ui-react';
import { type BaseUIComponentProps } from '../../internals/types';
import { useSelectRootContext, SelectLifecycleContext } from '../root/SelectRootContext';

/**
 * A portal element that moves the popup to a different part of the DOM.
 * By default, the portal element is appended to `<body>`.
 * Renders a `<div>` element.
 *
 * Documentation: [Base UI Select](https://base-ui.com/react/components/select)
 */
export const SelectPortal = React.forwardRef(function SelectPortal(
  portalProps: SelectPortal.Props,
  forwardedRef: React.ForwardedRef<HTMLDivElement>,
) {
  const store = useSelectRootContext();
  const { mounted } = React.useContext(SelectLifecycleContext);
  const forceMount = store.useState('forceMount');

  const shouldRender = mounted || forceMount;
  if (!shouldRender) {
    return null;
  }

  return <FloatingPortal ref={forwardedRef} {...portalProps} />;
});

export interface SelectPortalState {}

export interface SelectPortalProps extends BaseUIComponentProps<'div', SelectPortalState> {
  /**
   * A parent element to render the portal element into.
   */
  container?:
    HTMLElement | ShadowRoot | React.RefObject<HTMLElement | ShadowRoot | null> | null | undefined;
}

export namespace SelectPortal {
  export type State = SelectPortalState;
  export type Props = SelectPortalProps;
}
